import React, { Suspense, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HeaderProvider } from './context/HeaderContext';
import { LanguageSwitcherProvider } from './context/LanguageSwitcherProvider';
import { AppRoutes } from './routes';
import { LoadingIndicator } from './components/shared/LoadingIndicator';
import { CookieBanner } from './components/cookies/CookieBanner';
import { useCookieConsent } from './hooks/useCookieConsent';
import { useQuery } from '@tanstack/react-query';
import { configClient } from './lib/supabase/configClient';
import { AnalyticsRouteTracker } from './components/AnalyticsRouteTracker';
import ErrorBoundary from './components/analytics/ErrorBoundary';
import { analyticsService } from './lib/analytics/analyticsService';
import { ActiveAlertBanner } from './components/alerts/ActiveAlertBanner';
import { TestButtonsPanel } from './components/TestButtonsPanel';
import { useHotelConfig } from './hooks/useHotelConfig';

function App() {
  const { showBanner, acceptAll, rejectAll, savePreferences, dismissBanner, rgpdConfig } = useCookieConsent();
  const { id: hotelId, slug, baseUrl } = useHotelConfig();

  const { data: config, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: () => {
      return configClient.getConfig();
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  useEffect(() => {
    analyticsService.trackEvent({
      event_type: 'system',
      event_name: 'app_ready',
      event_data: {
        timestamp: new Date().toISOString(),
        is_pwa: window.matchMedia('(display-mode: standalone)').matches,
        hotel_id: hotelId,
        hotel_slug: slug,
        base_url: baseUrl
      }
    });

    return () => {
      analyticsService.trackEvent({
        event_type: 'system',
        event_name: 'app_unmount',
        event_data: { timestamp: new Date().toISOString() }
      });
    };
  }, [hotelId, baseUrl, slug]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement de la configuration</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary componentName="App">
      <HeaderProvider>
        <LanguageSwitcherProvider>
          <AnalyticsRouteTracker />
          <Suspense fallback={<LoadingIndicator id="app-suspense-loader" />}>
            <AppRoutes />
          </Suspense>

          {config && <ActiveAlertBanner config={config} />}

          {showBanner && config && rgpdConfig && (
            <CookieBanner
              config={rgpdConfig}
              appConfig={config}
              onAcceptAll={acceptAll}
              onRejectAll={rejectAll}
              onSavePreferences={savePreferences}
              onDismiss={dismissBanner}
            />
          )}

          <TestButtonsPanel />
        </LanguageSwitcherProvider>
      </HeaderProvider>
    </ErrorBoundary>
  );
}

export default App;